<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';

  import { getNotificationsContext } from '@client/components/notifications';
  import { Spinner } from '@client/components/spinner';

  import { SignatureRouteForm } from '@client/pages/signatures/routing/modals/shared';

  import { GroupsService } from '@client/services/groups';
  import { RoutingService } from '@client/services/routing';
  import { TagBlocksService } from '@client/services/tag-blocks';
  import { TagsService } from '@client/services/tags';

  import { appStore } from '@client/stores/app';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  let loading = true;
  let route = { priority: 0, hide: false, header: false, selectedGroups: [], selectedTags: [] };
  let senderGroups = [];
  let groupOptions = [];
  let tagOptions = [];
  let signaturesOptions = [];
  let filterText = '';
  let expanded = false;

  const addItem = (e) => {
    const currentTags = route?.tags || [];

    if (e.key === 'Enter') {
      if (route.selectedTags.map(({ value }) => value).includes(filterText)) {
        filterText = '';
        return;
      }

      if (!currentTags.includes(filterText) && filterText.length) {
        route.selectedTags = [{ label: filterText, value: filterText }, ...route.selectedTags];
        filterText = '';
      }
    } else {
      if (!filterText.length && e.detail.value) {
        const selected = e.detail.value;

        if (!currentTags.includes(selected) && selected.length) {
          route.selectedTags = [selected, ...route.selectedTags];
        }
      }
    }
  };

  const onSignatureChange = async (e) => {
    const blockname = e.detail.value;
    const response = await TagBlocksService.getById(blockname);

    route = {
      ...route,
      blockname,
      tagblock: response
    };
  };

  const executeCreate = async () => {
    $appStore.loading = true;

    try {
      const tags = (route.selectedTags || []).map(({ value }) => ({ text: value }));
      const selectedGroups = (route.selectedGroups || []).map(({ value }) => value);
      const sendergroups = senderGroups.filter(({ groupname }) => selectedGroups.includes(groupname));
      const { blockname, tagblock = {} } = route;

      const payload = {
        blockname,
        hide: route.hide,
        priority: 0,
        header: route.header,
        sendergroups,
        status: route.status,
        tagblock,
        tags
      };

      const response = await RoutingService.updateRoute(payload);

      // Get all routes and reorder to place new route at the top
      const allRoutes = await RoutingService.getAll();
      const ids = allRoutes.map(({ _id }) => _id);

      // Move the new route to the front
      const newRouteIndex = ids.indexOf(response._id);
      if (newRouteIndex > 0) {
        ids.splice(newRouteIndex, 1);
        ids.unshift(response._id);
        await RoutingService.saveRouteOrder(ids);
      }

      notify($format('label.SUCCESS.CREATE_ROUTE_FOR', { values: { blockname } }));

      step.data = {
        id: response.blockname,
        priority: response.priority,
        rollbackData: {
          routeId: response._id
        }
      };

      return { success: true };
    } catch (error) {
      notify($format('label.FAILURE.CREATE_ROUTE'), 'error');
      throw error;
    } finally {
      $appStore.loading = false;
    }
  };

  const rollbackCreate = async () => {
    $appStore.loading = true;

    try {
      const { routeId } = step.data.rollbackData;

      if (!routeId) {
        throw new Error($format('label.NO_ROLLBACK_DATA'));
      }

      // Get the route to delete
      const allRoutes = await RoutingService.getAll();
      const route = allRoutes.find((r) => r._id === routeId);

      if (!route) {
        throw new Error($format('label.FAILURE.ROUTE_NOT_FOUND'));
      }

      await RoutingService.deleteRoute(route);

      notify($format('label.SUCCESS.ROLLBACK_ROUTE'));

      return { success: true };
    } catch (error) {
      notify($format('label.FAILURE.ROLLBACK_ROUTE'), 'error');
      throw error;
    } finally {
      $appStore.loading = false;
    }
  };

  onMount(async () => {
    loading = true;

    try {
      const [groupsResponse, tagsResponse, signaturesResponse] = await Promise.all([
        GroupsService.getAll(),
        TagsService.getAll(),
        TagBlocksService.getAll()
      ]);

      senderGroups = Array.isArray(groupsResponse) ? groupsResponse : [];
      groupOptions = senderGroups.map((group) => ({
        label: group.groupname,
        value: group.groupname
      }));

      const tagList = Array.isArray(tagsResponse) ? tagsResponse : [];
      tagOptions = tagList.map((name) => ({ label: name, value: name }));

      const blocks = Array.isArray(signaturesResponse) ? signaturesResponse : [];
      signaturesOptions = blocks
        .filter(({ archived }) => !archived)
        .map(({ name, rulename }) => ({
          label: name || rulename,
          value: rulename
        }))
        .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
    } catch (error) {
      console.error('Failed to load signature routing data:', error);
      notify($format('label.FAILURE.LOAD_DATA'), 'error');
    } finally {
      loading = false;
    }

    if (onExecute) {
      onExecute(executeCreate);
    }

    if (onRollback) {
      onRollback(rollbackCreate);
    }
  });

  $: nextDisabled = !route.blockname || !route.selectedGroups?.length || loading;
</script>

<div class="signature-routing-create-form">
  {#if loading}
    <div class="loading">
      <Spinner show inline />
    </div>
  {:else}
    <SignatureRouteForm
      bind:route
      bind:signaturesOptions
      bind:groupOptions
      bind:tagOptions
      bind:filterText
      bind:expanded
      {onSignatureChange}
      {addItem}
    />
  {/if}
</div>

<style lang="scss">
  .signature-routing-create-form {
    .loading {
      align-items: center;
      display: flex;
      justify-content: center;
      padding: var(--padding-large);
    }
  }
</style>
